import { FC, ReactNode } from 'react';
import { OperatorRequest } from 'src/apis/contract-management/use-operator-by-tso';
import { DirectFcfsInfoTableRequest } from 'src/apis/direct-fcfs/use-direct-fcfs-info-table';
import { AuthenticatedMonolithUser } from 'src/apis/monolith/types';
import { OrganisationAssignmentsRequest } from 'src/apis/monolith/use-organisation-assignments';
import { TsoRequest } from 'src/apis/monolith/use-tso';
import { UserAssignmentRequest } from 'src/apis/monolith/use-user-assignment';
import { PublicOperatorDetailsRequest } from 'src/apis/organisation-service/use-public-operator-details';
import { AssignmentDocumentsRequest } from 'src/apis/shipper-registration/use-tso-assignment-documents';
import { TsoRegistrationContactRequest } from 'src/apis/shipper-registration/use-tso-registration-contact';
import { Card } from 'src/components/data-display/card';
import { TsoDocumentList } from 'src/components/domain-specifics/tso-document-list';
import { Stack } from 'src/components/layout/stack';
import { Link } from 'src/components/navigation/link';
import { SpinnerContainer } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { UsePortal } from 'src/hooks/use-portal';
import { useTitle } from 'src/hooks/use-title';
import { AssignmentStatus } from 'src/pages/market-information/players/operators/details/assignment-status';
import { ContactArea } from 'src/pages/market-information/players/operators/details/contact-area';
import { DirectFcFsTable } from 'src/pages/market-information/players/operators/details/direct-fcfs-table';
import { GeneralInformationArea } from 'src/pages/market-information/players/operators/details/general-information-area';
import { MarkDownArea } from 'src/pages/market-information/players/operators/details/markdown-area';
import { RegistrationDataArea } from 'src/pages/market-information/players/operators/details/registration-data-area';
import { Spacer } from 'src/pages/settings/registration/components/form-utils';
import { OptionalShipperRegistrationRequest } from 'src/pages/shippers/use-shipper-registration';

type Props = {
  actionPortal: UsePortal;
  tso: Successful<TsoRequest>;
  operatorDetails: Successful<PublicOperatorDetailsRequest>;
  shipperRegistration: OptionalShipperRegistrationRequest;
  tsoRegistrationContact: TsoRegistrationContactRequest;
  assignmentDocuments: Successful<AssignmentDocumentsRequest>;
  userAssignment?: Successful<UserAssignmentRequest>;
  organisationAssignments?: Successful<OrganisationAssignmentsRequest>;
  caMaOperator?: OperatorRequest;
  infoTableRequest?: DirectFcfsInfoTableRequest;
};

const Page: FC<Props> = ({
  actionPortal,
  tso,
  shipperRegistration,
  tsoRegistrationContact,
  assignmentDocuments,
  userAssignment,
  organisationAssignments,
  caMaOperator,
  infoTableRequest,
  operatorDetails,
}) => {
  useTitle(
    `Market Participants - ${operatorDetails.response.data.registeredAddress.companyName}`
  );
  const registrationInformationDocumentUrl =
    shipperRegistration.response?.data._links?.registrationInformationDocument
      ?.href;

  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const organisationAssignment = organisationAssignments?.response.data.find(
    (assignment) => assignment.tso.id === tso.response.data.id
  );

  const operatorDetailsResponse = operatorDetails.response.data;

  return (
    <SpinnerContainer pending={tso.pending}>
      {monolithUser?.isShipper &&
        organisationAssignment &&
        actionPortal.renderContent(<LinkToAssignments user={monolithUser} />)}

      <Stack gap={2}>
        <Card>
          <GeneralInformationArea
            operatorDetailsResponse={operatorDetailsResponse}
          />

          {tsoRegistrationContact.response ? (
            <RegistrationDataArea
              tsoRegistrationContact={tsoRegistrationContact.response.data}
            />
          ) : (
            <ContactArea tso={tso.response.data} />
          )}
        </Card>

        {operatorDetailsResponse.markdownContent && (
          <MarkDownArea
            markdownContent={operatorDetailsResponse.markdownContent}
          />
        )}

        {userAssignment && monolithUser && (
          <AssignmentStatus
            userAssignment={userAssignment}
            organisationAssignment={organisationAssignment}
            tso={tso}
            user={monolithUser}
            caMaOperator={caMaOperator}
          />
        )}

        <TsoDocumentList
          tso={tso.response.data}
          assignmentDocuments={assignmentDocuments.response.data}
          registrationInformationDocumentUrl={
            registrationInformationDocumentUrl
          }
        />

        <DirectFcFsTable infoTableRequest={infoTableRequest} />
      </Stack>
    </SpinnerContainer>
  );
};

export const RegistrationDataStack: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { minTablet } = useBreakpoints();
  return (
    <>
      <Spacer />
      <Stack
        templateColumns={minTablet ? '1fr 1fr 1fr' : '1fr'}
        gap={1.5}
        alignItems="start"
      >
        {children}
      </Stack>
      <Spacer />
    </>
  );
};

const LinkToAssignments: FC<{ user: AuthenticatedMonolithUser }> = ({
  user,
}) => {
  const userIsShipperAdmin = user.role === 'SHIPPER_ADMIN';
  if (userIsShipperAdmin) {
    return (
      <div data-testid="assignment-link">
        Go to{' '}
        <Link mode="default-underlined" to="/operators/assignments/overview">
          Assignments
        </Link>{' '}
        to edit your status.
      </div>
    );
  } else {
    return (
      <div data-testid="assignment-link">
        Go to{' '}
        <Link mode="default-underlined" to="/operators/assignments/overview">
          Assignments
        </Link>{' '}
        for further details.
      </div>
    );
  }
};

export default Page;
